import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadLogStatusChip from "./UploadLogStatusChip";
import { uploadLogService } from "../services/uploadLogService";

const UploadErrorDetailsDialog = ({
  open,
  onClose,
  uploadLog,
  onExportErrors,
  exportingErrors: exportingErrorsProp = false,
  onExportError,
}) => {
  const [internalExporting, setInternalExporting] = useState(false);

  if (!uploadLog) {
    return null;
  }

  const errorSummary = Array.isArray(uploadLog.errorSummary)
    ? uploadLog.errorSummary
    : [];
  const legacyErrors = Array.isArray(uploadLog.error_details)
    ? uploadLog.error_details
    : [];
  const totalErrors =
    uploadLog.failed_imports ??
    (errorSummary.length > 0
      ? errorSummary.reduce((sum, row) => sum + (row.count || 0), 0)
      : legacyErrors.length);

  const hasSummary = errorSummary.length > 0;
  const hasLegacy = legacyErrors.length > 0;
  const failedCount = Number(totalErrors) || 0;
  const allSucceeded = failedCount === 0;

  const canExportCsv = failedCount > 0 && (onExportErrors || uploadLog.id);
  const exportingErrors = onExportErrors
    ? exportingErrorsProp
    : internalExporting;

  const handleExportClick = async () => {
    if (onExportErrors) {
      await onExportErrors();
      return;
    }
    if (!uploadLog.id) return;
    setInternalExporting(true);
    try {
      const base =
        uploadLog.file_name?.replace(/[^\w.\-]+/g, "_") || "upload-errors";
      await uploadLogService.downloadErrorsCsvBlob(
        uploadLog.id,
        `upload-errors-${base}-${new Date().toISOString().split("T")[0]}.csv`,
      );
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (typeof error.response?.data === "string"
          ? error.response.data
          : null) ||
        error.message ||
        "Failed to export errors CSV";
      if (onExportError) {
        onExportError(message);
      } else {
        console.error("Upload error CSV export failed:", error);
      }
    } finally {
      setInternalExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxHeight: "80vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "#f5f5f5",
        }}
      >
        {allSucceeded ? (
          <CheckCircleOutlineIcon color="success" />
        ) : (
          <ErrorIcon color="error" />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">
            {allSucceeded ? "Upload Results" : "Upload Error Details"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {uploadLog.file_name}
            {allSucceeded
              ? ""
              : ` • ${failedCount} error${failedCount !== 1 ? "s" : ""}`}
          </Typography>
        </Box>
        {uploadLog.status ? (
          <UploadLogStatusChip status={uploadLog.status} />
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Chip
              label={`${uploadLog.successful_imports ?? 0} Successful`}
              color="success"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${uploadLog.failed_imports ?? totalErrors} Failed rows`}
              color="error"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${uploadLog.total_rows ?? 0} Total Rows`}
              color="default"
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {allSucceeded && !hasSummary && !hasLegacy ? (
          <Typography variant="body2" color="text.secondary">
            All rows were imported successfully.
          </Typography>
        ) : null}

        {hasSummary ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell>Error message</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell>Sample rows</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorSummary.map((row) => (
                  <TableRow key={row.message} hover>
                    <TableCell>
                      <Typography variant="body2">{row.message}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.count}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.sample_row_numbers?.length ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.sample_row_numbers.join(", ")}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Failed rows
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell>Row</TableCell>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Blocking sync</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {legacyErrors.map((row, index) => (
                    <TableRow key={`${row.row_number || index}-${row.error_message}`} hover>
                      <TableCell>{row.row_number ?? '—'}</TableCell>
                      <TableCell>
                        {row.row_data?.identifier ||
                          row.row_data?.sku ||
                          row.row_data?.article_code ||
                          '—'}
                      </TableCell>
                      <TableCell>
                        {row.row_data?.blocking_sync_id ? (
                          <Box>
                            <Link
                              href={`/inventory-sync/${row.row_data.blocking_sync_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Sync #{row.row_data.blocking_sync_id}
                            </Link>
                            {row.row_data?.blocking_org_id ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Shop #{row.row_data.blocking_org_id}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.error_message}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#f8f9fa" }}>
        {canExportCsv ? (
          <Button
            onClick={handleExportClick}
            variant="outlined"
            color="error"
            startIcon={exportingErrors ? null : <DownloadIcon />}
            disabled={exportingErrors}
          >
            {exportingErrors ? "Exporting..." : "Export Errors (CSV)"}
          </Button>
        ) : null}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadErrorDetailsDialog;
